import React, { useState, useEffect } from "react";
import {
  ArrowLeft,
  Edit2,
  User,
  Calendar as CalendarIcon,
  DollarSign,
  TrendingUp,
  MapPin,
  CheckCircle,
  PlusCircle,
  Layers,
  Shield,
} from "lucide-react";
import { useHistory, useParams } from "react-router-dom";
import moment from "moment";
import axios from "axios";
import { MyDomain } from "../../../App";
import BossUpdateEmployeeModal from "../BossEmployeeComps/BossUpdateEmployeeModal";
import BossCreateDayWork from "../BossDailyWorkComps/BossCreateDailyWork";

export default function BossStatisticEmployeeData() {
  const history = useHistory();
  const { employeeId } = useParams();

  // Get data from localStorage
  const [choosedEmployee, setChoosedEmployee] = useState(null);
  const [allData, setAllData] = useState(null);
  const [userData, setUserData] = useState(null);
  const [choosedMonth, setChoosedMonth] = useState(null);
  const [loading, setLoading] = useState(true);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedPlaceId, setSelectedPlaceId] = useState(null);
  const [existingEmployees, setExistingEmployees] = useState([]);

  useEffect(() => {
    // Load data from localStorage
    const employee = localStorage.getItem("choosedEmployee");
    const data = localStorage.getItem("allData");
    const user = localStorage.getItem("user");
    const month =
      localStorage.getItem("choosedMonth") || moment().format("MM-YYYY");

    if (employee) setChoosedEmployee(JSON.parse(employee));
    if (data) setAllData(JSON.parse(data));
    if (user) setUserData(JSON.parse(user));
    setChoosedMonth(month);
    setLoading(false);
  }, []);

  // Save chosen month to localStorage when it changes
  useEffect(() => {
    if (choosedMonth) {
      localStorage.setItem("choosedMonth", choosedMonth);
    }
  }, [choosedMonth]);

  // Reload data after modal success
  const handleCreateSuccess = async () => {
    setShowCreateModal(false);
    setExistingEmployees([]);
    setSelectedPlaceId(null);

    // Refresh data from API
    if (userData) {
      try {
        const response = await axios.post(`${MyDomain}Employee/GetAllData`, {
          UserId: userData.id,
        });
        setAllData(response.data || {});
        localStorage.setItem("allData", JSON.stringify(response.data));
      } catch (err) {
        console.error("❌ Error fetching data:", err);
      }
    }
  };

  const handleUpdateSuccess = async () => {
    setShowEditModal(false);

    // Refresh data from API
    if (userData) {
      try {
        const response = await axios.post(`${MyDomain}Employee/GetAllData`, {
          UserId: userData.id,
        });
        setAllData(response.data || {});
        localStorage.setItem("allData", JSON.stringify(response.data));

        // Update the chosen employee with latest data
        const updatedEmployee = response.data.myEmployees?.find(
          (emp) => emp.id === choosedEmployee.id
        );
        if (updatedEmployee) {
          setChoosedEmployee(updatedEmployee);
          localStorage.setItem(
            "choosedEmployee",
            JSON.stringify(updatedEmployee)
          );
        }
      } catch (err) {
        console.error("❌ Error fetching data:", err);
      }
    }
  };

  if (loading || !choosedEmployee || !allData) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Determine if employee has monthly salary
  const isMonthlyEmployee = choosedEmployee?.typeSalary === 2;
  const employeeSalary = isMonthlyEmployee
    ? choosedEmployee?.monthlySalary || 0
    : choosedEmployee?.salary || 0;

  // Create array of months (12 months back)
  const generateMonths = () => {
    const months = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const y = d.getFullYear();
      months.push(`${m}-${y}`);
    }
    return months;
  };

  const monthList = generateMonths();

  // FILTER DATA BY EMPLOYEE + MONTH
  const filteredDailyWork = (allData?.myDailyWork || []).filter((item) => {
    if (!choosedEmployee?.id || !choosedMonth) return false;

    const [day, month, year] = item.date.split("-");
    const itemMonth = `${month}-${year}`;

    return item.employeeId === choosedEmployee.id && itemMonth === choosedMonth;
  });

  // Group work data by date
  const groupByDate = () => {
    const grouped = {};

    filteredDailyWork.forEach((item) => {
      if (!grouped[item.date]) {
        grouped[item.date] = [];
      }
      grouped[item.date].push(item);
    });

    return grouped;
  };

  const groupedData = groupByDate();

  // Get all days in the selected month
  const getAllDaysInMonth = () => {
    if (!choosedMonth) return [];

    const [month, year] = choosedMonth.split("-");
    const firstDay = moment(`${year}-${month}-01`, "YYYY-MM-DD");
    const daysInMonth = firstDay.daysInMonth();

    const allDays = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const dayStr = String(day).padStart(2, "0");
      const dateStr = `${dayStr}-${month}-${year}`;
      allDays.push(dateStr);
    }

    return allDays;
  };

  const allDaysInMonth = getAllDaysInMonth();

  // Calculate daily totals for this employee
  const getDayTotal = (date) => {
    const dayData = groupedData[date] || [];
    return dayData.reduce(
      (sum, item) => sum + (parseFloat(item.salary) || 0),
      0
    );
  };

  // Count working days
  const workingDays = Object.keys(groupedData).length;

  // Calculate month total salary for this employee
  const calculateTotalSalary = () => {
    if (isMonthlyEmployee) {
      return workingDays > 0 ? employeeSalary : 0;
    } else {
      return filteredDailyWork.reduce(
        (sum, item) => sum + (parseFloat(item.salary) || 0),
        0
      );
    }
  };

  const totalSalary = calculateTotalSalary();

  // Calculate average daily salary
  const calculateAvgDailySalary = () => {
    if (isMonthlyEmployee) {
      return workingDays > 0 ? employeeSalary / workingDays : 0;
    } else {
      return workingDays > 0 ? totalSalary / workingDays : 0;
    }
  };

  const avgDailySalary = calculateAvgDailySalary();

  // Get place name by ID
  const getPlaceName = (placeId) => {
    const place = allData.myPlaces?.find((p) => p.id === placeId);
    return place?.place || `Place ${placeId}`;
  };

  // Get manager name
  const getManagerName = (managerId) => {
    const manager = allData.myEmployees?.find((e) => e.id === managerId);
    return manager?.name || "Me";
  };

  // Get day name
  const getDayName = (date) => {
    return moment(date, "DD-MM-YYYY").format("ddd");
  };

  // Handle day click to open modal
  const handleDayClick = (date) => {
    const dayData = groupedData[date] || [];

    if (dayData.length > 0) {
      // Has existing work - load it for editing
      const workEntry = dayData[0];
      const placeId = workEntry.placeId;

      const allEmployeesThisDay = allData.myDailyWork.filter(
        (item) => item.date === date && item.placeId === placeId
      );

      const existingEmps = allEmployeesThisDay.map((item) => {
        const employee = allData.myEmployees.find(
          (e) => e.id === item.employeeId
        );
        return {
          id: item.employeeId,
          name: employee?.name || `Employee ${item.employeeId}`,
          salary: item.salary.toString(),
        };
      });

      setSelectedDate(date);
      setSelectedPlaceId(placeId);
      setExistingEmployees(existingEmps);
    } else {
      // No existing work - create new
      setSelectedDate(date);
      setSelectedPlaceId(null);
      setExistingEmployees([
        {
          id: choosedEmployee.id,
          name: choosedEmployee.name,
          salary: isMonthlyEmployee ? "0" : employeeSalary.toString(),
        },
      ]);
    }

    setShowCreateModal(true);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white px-6 pt-8 pb-6 rounded-b-3xl shadow-md">
        {/* Top Bar */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => history.goBack()}
            className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition"
          >
            <ArrowLeft className="w-5 h-5 text-gray-900" />
          </button>

          <button
            className="w-10 h-10 bg-purple-50 hover:bg-purple-100 rounded-full flex items-center justify-center transition"
            onClick={() => setShowEditModal(true)}
          >
            <Edit2 className="w-5 h-5 text-purple-600" />
          </button>
        </div>

        {/* Employee Info */}
        <div className="flex items-center gap-4 mb-4">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
            <User className="w-8 h-8 text-white" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900 mb-1">
              {choosedEmployee?.name}
            </h1>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Employee Statistics</span>
              <div
                className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-semibold ${
                  isMonthlyEmployee
                    ? "bg-purple-100 text-purple-700"
                    : "bg-green-100 text-green-700"
                }`}
              >
                <CalendarIcon className="w-3 h-3" />
                <span>{isMonthlyEmployee ? "Monthly" : "Daily"}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Base Salary Info */}
        <div className="bg-gray-50 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">
                {isMonthlyEmployee
                  ? "Base Monthly Salary"
                  : "Base Daily Salary"}
              </span>
            </div>
            <span className="text-lg font-bold text-gray-900">
              ₪{employeeSalary.toLocaleString()}
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-6 space-y-6 pb-24">
        {/* Month Selector */}
        <div>
          <h2 className="text-lg font-bold text-gray-900 mb-4">Select Month</h2>
          <div className="flex gap-3 overflow-x-auto pb-2 hide-scrollbar">
            {monthList.map((item) => {
              const [month, year] = item.split("-");
              const monthName = moment(item, "MM-YYYY").format("MMM");
              const isSelected = choosedMonth === item;

              return (
                <button
                  key={item}
                  onClick={() => setChoosedMonth(item)}
                  className={`flex-shrink-0 px-5 py-3 rounded-2xl border-2 min-w-[90px] transition-all ${
                    isSelected
                      ? "bg-blue-600 border-blue-600 text-white shadow-lg"
                      : "bg-white border-gray-200 text-gray-700 hover:border-blue-300"
                  }`}
                >
                  <div className="text-center">
                    <p
                      className={`text-base font-semibold ${
                        isSelected ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {monthName}
                    </p>
                    <p
                      className={`text-xs ${
                        isSelected ? "text-blue-100" : "text-gray-500"
                      }`}
                    >
                      {year}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Working Days Card */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border-l-4 border-blue-500">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <CalendarIcon className="w-6 h-6 text-blue-600" />
            </div>
            <p className="text-2xl font-bold text-gray-900 mb-1">
              {workingDays}
            </p>
            <p className="text-sm text-gray-600 font-medium">Working Days</p>
          </div>

          {/* Total Salary Card */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border-l-4 border-green-500">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
            <p className="text-2xl font-bold text-gray-900 mb-1">
              ₪{totalSalary.toLocaleString()}
            </p>
            <p className="text-sm text-gray-600 font-medium">
              {isMonthlyEmployee ? "Monthly Salary" : "Total Earned"}
            </p>
          </div>
        </div>

        {/* Average Card */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 font-medium mb-2">
                {isMonthlyEmployee
                  ? "Avg Per Working Day"
                  : "Average Daily Salary"}
              </p>
              <p className="text-3xl font-bold text-gray-900 mb-1">
                ₪{avgDailySalary.toFixed(2)}
              </p>
              {isMonthlyEmployee && workingDays > 0 && (
                <p className="text-xs text-gray-500 italic">
                  ₪{employeeSalary.toLocaleString()} ÷ {workingDays} days
                </p>
              )}
            </div>
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
              <TrendingUp className="w-8 h-8 text-purple-600" />
            </div>
          </div>
        </div>

        {/* Work History */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900">Work History</h2>
            {choosedMonth && (
              <div className="bg-blue-50 px-4 py-2 rounded-xl">
                <span className="text-sm font-semibold text-blue-600">
                  {moment(choosedMonth, "MM-YYYY").format("MMMM YYYY")}
                </span>
              </div>
            )}
          </div>

          {allDaysInMonth.length === 0 ? (
            // Empty State
            <div className="bg-white rounded-2xl p-12 text-center">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CalendarIcon className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No Month Selected
              </h3>
              <p className="text-sm text-gray-500">
                Select a month above to view work history
              </p>
            </div>
          ) : (
            // Calendar Grid
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-7 gap-2">
              {allDaysInMonth.map((date) => {
                const dayData = groupedData[date] || [];
                const dayTotal = getDayTotal(date);
                const hasWork = dayData.length > 0;
                const isToday = moment(date, "DD-MM-YYYY").isSame(
                  moment(),
                  "day"
                );
                const dayNumber = moment(date, "DD-MM-YYYY").format("DD");
                const dayName = getDayName(date);

                return (
                  <button
                    key={date}
                    onClick={() => handleDayClick(date)}
                    className={`relative bg-white rounded-xl p-3 min-h-[100px] transition-all border-2 ${
                      hasWork
                        ? "border-green-500 hover:shadow-md"
                        : "border-gray-200 hover:border-gray-300"
                    } ${isToday ? "ring-2 ring-blue-500 border-blue-500" : ""}`}
                  >
                    {/* Date Header */}
                    <div className="flex justify-between items-start mb-2 pb-2 border-b border-gray-100">
                      <span
                        className={`text-lg font-bold ${
                          hasWork
                            ? "text-green-600"
                            : isToday
                            ? "text-blue-600"
                            : "text-gray-400"
                        }`}
                      >
                        {dayNumber}
                      </span>
                      <span
                        className={`text-[10px] font-semibold uppercase ${
                          hasWork ? "text-gray-600" : "text-gray-400"
                        }`}
                      >
                        {dayName}
                      </span>
                    </div>

                    {/* Content */}
                    {hasWork ? (
                      <div className="space-y-1">
                        {/* Place */}
                        <div className="flex items-center gap-1">
                          <MapPin className="w-3 h-3 text-green-600 flex-shrink-0" />
                          <span className="text-[10px] font-semibold text-gray-900 truncate">
                            {getPlaceName(dayData[0].placeId)}
                          </span>
                        </div>

                        {/* Salary for daily employees */}
                        {!isMonthlyEmployee && (
                          <div className="bg-rose-50 px-2 py-1 rounded">
                            <span className="text-xs font-bold text-rose-600">
                              ₪{dayTotal.toFixed(0)}
                            </span>
                          </div>
                        )}

                        {/* Monthly employee worked badge */}
                        {isMonthlyEmployee && (
                          <div className="flex items-center gap-1 bg-purple-50 px-2 py-1 rounded">
                            <CheckCircle className="w-3 h-3 text-purple-600" />
                            <span className="text-[10px] font-semibold text-purple-600">
                              Worked
                            </span>
                          </div>
                        )}

                        {/* Multiple entries or manager */}
                        {dayData.length > 1 && (
                          <div className="flex items-center gap-1">
                            <Layers className="w-3 h-3 text-gray-600" />
                            <span className="text-[10px] font-semibold text-gray-600">
                              {dayData.length}
                            </span>
                          </div>
                        )}

                        {dayData[0].whoMake && (
                          <div className="flex items-center gap-1 bg-purple-50 px-1 py-0.5 rounded">
                            <Shield className="w-2 h-2 text-purple-600" />
                            <span className="text-[8px] font-semibold text-purple-600 truncate">
                              {getManagerName(dayData[0].whoMake)}
                            </span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-2">
                        <PlusCircle className="w-7 h-7 text-gray-300 mb-1" />
                        <span className="text-[10px] font-medium text-gray-400">
                          No Work
                        </span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {/* Create/Edit Day Work Modal */}
      <BossCreateDayWork
        visible={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setExistingEmployees([]);
          setSelectedPlaceId(null);
        }}
        onSuccess={handleCreateSuccess}
        places={allData.myPlaces}
        employees={allData.myEmployees}
        preselectedDate={selectedDate}
        preselectedPlaceId={selectedPlaceId}
        preselectedEmployees={existingEmployees}
      />

      {/* Update Employee Modal */}
      <BossUpdateEmployeeModal
        visible={showEditModal}
        onClose={() => setShowEditModal(false)}
        employee={choosedEmployee}
        onUpdateSuccess={handleUpdateSuccess}
      />

      <style>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
}
